// src/domains/subs.ts —— 06 A-03 下级写动作（新增 / 重置下级密码 / 停用｜启用 / 删除）、「下级售价」只读弹层（0045）与 A-08 修改我的密码弹层（切片 s.sb）
// 0045 代理加价定价：返佣费率废弃——新增下级不再收费率、调整费率弹层删除（PUT /api/agent/sub-agents/{id} 下线），代理只能查看下级售价
import { ApiError } from '../../../shared/src/api/client';
import type { AgentPricesView } from '../../../shared/src/api/types';
import { I18N_SUBS, REWRITES_SUBS } from '../i18n/subs';
import type { AgentState } from '../state';
import type { SubAgentRow } from '../types';
import { loadActiveSubs, loadOverview } from './home';
import { readonlyPriceRows, uplineText } from './prices';
import { KEYS, MODAL_KEYS } from './subs.keys';
import { reloadPage } from './table';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';

// 07 §3 / X-24：8–64 位且同时含字母与数字（与后端 pwd.ValidatePolicy 同口径）
const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 后端 agent.normalizeAccount：ASCII、转小写后 ^[a-z0-9._-]{1,64}$（前端预检，避免 4220 原文透出）
const ACCOUNT_RE = /^[A-Za-z0-9._-]{1,64}$/;
const NAME_MAX = 64;

export interface SubForm {
  name: string;
  email: string;
  acct: string;
  pwd: string;
  tg: string;
}

export interface PwdForm {
  old: string;
  nw: string;
  nw2: string;
}

// 下级售价只读弹层（GET /api/agent/sub-agents/{id}/prices）：id 用于丢弃换弹层后的迟到响应
export interface SubPricesState {
  id: number;
  view: AgentPricesView | null;
  loading: boolean;
}

export interface SubsSlice {
  form: SubForm;
  pwd: PwdForm;
  formError: string;
  confirm: { id: number; label: string } | null;
  target: { id: number; name: string } | null;
  prices: SubPricesState | null;
}

declare module '../state' {
  interface AgentSlices {
    sb?: SubsSlice;
  }
}

const FORM0: SubForm = { name: '', email: '', acct: '', pwd: '', tg: '' };
const PWD0: PwdForm = { old: '', nw: '', nw2: '' };

function fresh(): SubsSlice {
  return { form: { ...FORM0 }, pwd: { ...PWD0 }, formError: '', confirm: null, target: null, prices: null };
}

const SB0: SubsSlice = fresh();

export function subsOf(s: AgentState): SubsSlice {
  return s.sb ?? SB0;
}

function setSb(ctx: AgentCtx, patch: Partial<SubsSlice>): void {
  ctx.host.setState({ sb: { ...subsOf(ctx.host.state), ...patch } });
}

// 写成功后：当前页 + 导航徽标 + overview（kpi.sub_agents 随下级增删变化）
async function reloadSubs(ctx: AgentCtx): Promise<void> {
  await Promise.all([reloadPage(ctx, 'subs'), loadActiveSubs(ctx), loadOverview(ctx)]);
}

// 表单弹层失败 → 底部红条；行内动作 / 确认框失败 → toast（06 A-00 / TD-843）；1305（本人不可再新增下级）一律 toast 中性文案 noSubTip 并关弹层（06 A-03）。
// 会话在请求期间结束（epoch 变化）→ 结果作废，不 toast、不写切片
async function write(ctx: AgentCtx, call: () => Promise<unknown>, okKey: string, opts: { inModal: boolean }): Promise<boolean> {
  const ep = ctx.gp.session.epoch();
  const done = await ctx.gp.ui.run(async () => {
    try {
      await call();
      return true;
    } catch (e) {
      if (ctx.gp.session.epoch() !== ep) return false;
      if (e instanceof ApiError && e.code === 1305) {
        ctx.host.setState({ modal: null });
        ctx.gp.ui.toast(ctx.gp.ui.err(e));
      } else if (opts.inModal) setSb(ctx, { formError: ctx.gp.ui.err(e) });
      else ctx.gp.ui.toast(ctx.gp.ui.err(e));
      return false;
    }
  });
  if (done !== true || ctx.gp.session.epoch() !== ep) return false;
  ctx.gp.ui.toast(str(ctx.t, okKey));
  return true;
}

export function openNewSub(ctx: AgentCtx): void {
  const t = ctx.t;
  ctx.host.setState({ modal: { type: 'newSub', kind: str(t, 'subs'), title: str(t, 'newSub') }, sb: { ...subsOf(ctx.host.state), form: { ...FORM0 }, formError: '', target: null } });
}

// 0045 下级售价只读弹层：先开弹层（加载态），再取 GET /api/agent/sub-agents/{id}/prices；
// 会话已换或弹层已换成别的下级 / 已关闭则丢弃响应；孙级 1501 / 非子树 4040 等失败显示在弹层红条（ui.err）
export async function openSubPrices(ctx: AgentCtx, r: SubAgentRow): Promise<void> {
  const t = ctx.t;
  ctx.host.setState({
    modal: { type: 'subPrices', kind: str(t, 'subs'), title: `${r.name} · ${str(t, 'aSubPrices')}`, id: r.id },
    sb: { ...subsOf(ctx.host.state), formError: '', target: { id: r.id, name: r.name }, prices: { id: r.id, view: null, loading: true } },
  });
  const ep = ctx.gp.session.epoch();
  const still = (): boolean => ctx.gp.session.epoch() === ep && ctx.host.state.modal?.type === 'subPrices' && subsOf(ctx.host.state).prices?.id === r.id;
  try {
    const view = await ctx.gp.api.call<AgentPricesView>('GET /api/agent/sub-agents/{id}/prices', { path: { id: r.id } });
    if (still()) setSb(ctx, { prices: { id: r.id, view, loading: false } });
  } catch (e) {
    if (still()) setSb(ctx, { prices: { id: r.id, view: null, loading: false }, formError: ctx.gp.ui.err(e) });
  }
}

export function openSubPwd(ctx: AgentCtx, r: SubAgentRow): void {
  const t = ctx.t;
  ctx.host.setState({
    modal: { type: 'subPwd', kind: str(t, 'subs'), title: `${str(t, 'subPwdTitle')} · ${r.name}`, id: r.id },
    sb: { ...subsOf(ctx.host.state), pwd: { ...PWD0 }, formError: '', target: { id: r.id, name: r.name } },
  });
}

export function askDelete(ctx: AgentCtx, r: SubAgentRow): void {
  setSb(ctx, { confirm: { id: r.id, label: `${r.name} · ${r.biz_no}` } });
}

export async function toggleSub(ctx: AgentCtx, r: SubAgentRow): Promise<void> {
  const okKey = r.status === 'active' ? 'toastDisabled' : 'toastEnabled';
  const ok = await write(ctx, () => ctx.gp.api.call('POST /api/agent/sub-agents/{id}/toggle', { path: { id: r.id } }), okKey, { inModal: false });
  if (ok) await reloadSubs(ctx);
}

async function createSub(ctx: AgentCtx): Promise<void> {
  const f = subsOf(ctx.host.state).form;
  const t = ctx.t;
  const name = f.name.trim();
  const email = f.email.trim();
  const acct = f.acct.trim();
  // 06 A-03 前端校验顺序：姓名 + 邮箱 → 账号 + 密码规则
  if (!name || [...name].length > NAME_MAX || !EMAIL_RE.test(email)) return setSb(ctx, { formError: str(t, 'errName') });
  if (!ACCOUNT_RE.test(acct) || !PWD_RE.test(f.pwd)) return setSb(ctx, { formError: str(t, 'errAcct') });
  // agent.CreateInput：代理端不传 telegram_id / parent_agent_id（父恒为会话代理，M-05）；0045 不再带 open_rate / spend_rate（严格解码 4220），
  // 新下级售价全部继承我的生效价，由其本人在「我的售价」设置
  const body = { name, email, login_account: acct, password: f.pwd, telegram_username: f.tg.trim() };
  const ok = await write(ctx, () => ctx.gp.api.call('POST /api/agent/sub-agents', { body }), 'toastCreated', { inModal: true });
  if (!ok) return;
  ctx.host.setState({ modal: null });
  await reloadSubs(ctx);
}

async function savePwd(ctx: AgentCtx): Promise<void> {
  const s = ctx.host.state;
  const sb = subsOf(s);
  const p = sb.pwd;
  const t = ctx.t;
  const forSub = s.modal?.type === 'subPwd';
  if (!forSub && !p.old) return setSb(ctx, { formError: str(t, 'errOldPwdEmpty') });
  if (!PWD_RE.test(p.nw) || p.nw !== p.nw2) return setSb(ctx, { formError: str(t, 'errPwdRule') });
  if (forSub) {
    // AP M-8：上级为下级设定新密码（下级下次登录强制改密），系统邮件通知下级
    const id = sb.target?.id;
    if (id === undefined) return;
    const ok = await write(ctx, () => ctx.gp.api.call('POST /api/agent/sub-agents/{id}/reset-password', { path: { id }, body: { password: p.nw } }), 'toastSubPwd', { inModal: true });
    if (ok) ctx.host.setState({ modal: null, sb: { ...subsOf(ctx.host.state), pwd: { ...PWD0 } } });
    return;
  }
  const ep = ctx.gp.session.epoch();
  const done = await ctx.gp.ui.run(async () => {
    try {
      await ctx.gp.api.call('PUT /api/agent/password', { body: { old: p.old, new: p.nw } });
      return true;
    } catch (e) {
      if (ctx.gp.session.epoch() === ep) setSb(ctx, { formError: e instanceof ApiError && e.code === 1001 ? str(t, 'errOldPwd') : ctx.gp.ui.err(e) });
      return false;
    }
  });
  if (done !== true) return;
  // 06 A-08 / S-97：改密吊销全部会话（含当前）→ 清 token 回登录页重新登录
  ctx.gp.session.passwordChanged();
}

async function doConfirm(ctx: AgentCtx): Promise<void> {
  const c = subsOf(ctx.host.state).confirm;
  if (!c) return;
  // X-10：服务端前置 1304（有余额 / 未结算 / 在途提佣）→ toast errSubDelBlocked
  const ok = await write(ctx, () => ctx.gp.api.call('DELETE /api/agent/sub-agents/{id}', { path: { id: c.id } }), 'toastDeleted', { inModal: false });
  setSb(ctx, { confirm: null });
  if (!ok) return;
  ctx.host.setState({ modal: null });
  await reloadSubs(ctx);
}

const onField = (ctx: AgentCtx, k: keyof SubForm) => (e: InputEvent) => {
  const sb = subsOf(ctx.host.state);
  setSb(ctx, { form: { ...sb.form, [k]: e.target.value }, formError: '' });
};

const onPwdField = (ctx: AgentCtx, k: keyof PwdForm) => (e: InputEvent) => {
  const sb = subsOf(ctx.host.state);
  setSb(ctx, { pwd: { ...sb.pwd, [k]: e.target.value }, formError: '' });
};

export const subs: AgentDomain = {
  name: 'subs',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_SUBS,
  rewrites: REWRITES_SUBS,
  state: () => ({ sb: fresh() }),
  mount(ctx) {
    // 会话结束即清空表单（含密码草稿）与确认框：下一账号不得看到上一账号的输入
    ctx.events.on('session:lost', () => ctx.host.setState({ sb: fresh() }));
  },
  update(ctx, prev) {
    // 外壳打开「修改密码」时复位改密表单；任一弹层关闭时清红条
    const now = ctx.s.modal?.type ?? '';
    const was = prev.modal?.type ?? '';
    if (now === 'myPwd' && was !== 'myPwd') setSb(ctx, { pwd: { ...PWD0 }, formError: '', target: null });
    else if (!now && was && subsOf(ctx.s).formError) setSb(ctx, { formError: '' });
  },
  vals(ctx) {
    const sb = subsOf(ctx.s);
    const sp = sb.prices;
    return {
      form: sb.form,
      onForm: {
        name: onField(ctx, 'name'),
        email: onField(ctx, 'email'),
        acct: onField(ctx, 'acct'),
        pwd: onField(ctx, 'pwd'),
        tg: onField(ctx, 'tg'),
      },
      formError: sb.formError,
      createSub: () => void createSub(ctx),
      savePwd: () => void savePwd(ctx),
      pwd: sb.pwd,
      onPwd: { old: onPwdField(ctx, 'old'), nw: onPwdField(ctx, 'nw'), nw2: onPwdField(ctx, 'nw2') },
      notSubPwd: ctx.s.modal?.type !== 'subPwd',
      // 0190 片 dev:A-03：下级售价只读弹层（行口径与「我的售价」同源，readonlyPriceRows）
      subPrices: {
        tip: str(ctx.t, 'subPricesTip'),
        close: str(ctx.t, 'subPricesClose'),
        loading: sp?.loading ?? false,
        upline: uplineText(ctx.t, sp?.view ?? null),
        rows: readonlyPriceRows(ctx.t, ctx.lang, sp?.view ?? null),
      },
      confirmOpen: sb.confirm !== null,
      confirmLabel: sb.confirm?.label ?? '',
      closeConfirm: () => setSb(ctx, { confirm: null }),
      doConfirm: () => void doConfirm(ctx),
    };
  },
  modal(ctx) {
    const type = ctx.s.modal?.type ?? '';
    return { isNewSub: type === 'newSub', isMyPwd: type === 'myPwd' || type === 'subPwd', isSubPrices: type === 'subPrices' };
  },
};
